import { useQuery } from "@tanstack/react-query";
import { ProctoringOptions } from "@verity/components";
import { FlaggedKeyword } from "@verity/database/prisma";
import { Application, Extension, ProctoringOption } from "@verity/proctoring-option/types";
import { Dispatch, SetStateAction, useContext } from "react";
import { isProctoringOptionCategory } from "../../../../../../../../packages/components/src/editQuiz/proctoring-options/option-text/ProctoringOptionText";
import apiClient from "../../../../../helpers/api-client";
import i18n from "../../../../../i18n";
import { PortalControllerContext } from "../../../../context";
import ContentWrapper from "../components/ContentWrapper";
import ConfigurationsSkeleton from "./ConfigurationsSkeleton";

type ConfigurationsProps = {
	proctoringOption: Partial<ProctoringOption>;
	setProctoringOption: Dispatch<SetStateAction<Partial<ProctoringOption>>>;
	subpage: string;
};

export default function Configurations({ proctoringOption, setProctoringOption, subpage }: ConfigurationsProps) {
	const { instituteSettings, verityInstituteSettings, courseSettings } = useContext(PortalControllerContext);

	const {
		data: applications,
		error: applicationsError,
		isLoading: applicationsLoading,
	} = useQuery<Application[]>({
		queryKey: ["applications"],
		queryFn: async () => await apiClient.verityOptions.getAllApplications(),
	});

	const {
		data: extensions,
		error: extensionsError,
		isLoading: extensionsLoading,
	} = useQuery<Extension[]>({
		queryKey: ["extensions"],
		queryFn: async () => await apiClient.verityOptions.getAllExtensions(),
	});

	if (applicationsLoading || extensionsLoading) return <ConfigurationsSkeleton />;

	if (applicationsError || extensionsError) throw new Error(applicationsError?.message || extensionsError?.message);

	if (!applications || !extensions || !isProctoringOptionCategory(subpage)) return null;

	return (
		<ContentWrapper
			title={i18n.t("candidate.scheduleInterview.configurationStep.title")}
			description={i18n.t("candidate.scheduleInterview.configurationStep.description")}
		>
			<ProctoringOptions
				data={{
					applications,
					extensions,
					instituteKeywords: instituteSettings.flaggedKeywords as FlaggedKeyword[],
					geofences: courseSettings.geofenceLocations,
				}}
				currentCategory={subpage}
				enabledProctoringOptions={{ proctoringMethod: ["live"] }}
				enforcedProfilesOnly={false}
				proctoringOption={proctoringOption}
				setProctoringOption={(options) => setProctoringOption(options)}
				verityInstituteSettings={verityInstituteSettings}
				optionRelatedInstituteSettings={instituteSettings}
			/>
		</ContentWrapper>
	);
}
